import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import {
  exportEventsCSV,
  exportEventsJSON,
  exportEventsPDF,
  getAiSystemLogUsers,
  getEvents,
  getTrends
} from '../../../../../services/SystemLoggingService';
import { LogEntry } from '../../../../../types/SystemLogging.types';
import {
  buildUserFilterOptions,
  splitUserFilterValues
} from '../../../../../utils/LogUserFilter.util';
import { AppDispatch, RootState } from '../../../../../store';
import {
  resetFilters,
  setDateRangeFilter,
  setSearchFilter,
  setSorting,
  setTrendsError,
  setTrendsLoading,
  setTrendsSuccess,
  setTypesFilter,
  setUserFilter
} from '../../../../../store/slices/systemLoggingSlice';
import { triggerBlobDownload } from '../../../../../utils/File.util';
import { AuditLogsTable } from '../../../../tables/AuditLogsTable/AuditLogsTable';
import { SearchSection } from '../../../SearchSection/SearchSection';
import { EventsCard } from '../EventsCard/EventsCard';
import { ExportDropdown } from '../ExportDropdown/ExportDropdown';
import { MonitoringFiltersSection } from '../MonitoringFiltersSection/MonitoringFiltersSection';
import { OverviewChartCard } from '../OverviewChartCard/OverviewChartCard';
import { TrendsChart } from '../TrendsChart/TrendsChart';
import { SideDrawer } from '../../../../ui/SideDrawer/SideDrawer';
import { getAuditLogDrawerContent } from '../../../AuditLogsManagement/AuditLogDrawerContent';

const EVENTS_PAGE_SIZE = 10;

export function TechnicalMonitoring() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const hasOpenedEventFromNavigation = useRef(false);

  const { filters, sorting } = useSelector(
    (state: RootState) => state.systemLogging
  );

  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<LogEntry | null>(null);
  const [openSideDrawer, setOpenSideDrawer] = useState(false);

  const [userOptions, setUserOptions] = useState<{ value: string; label: string }[]>(
    []
  );

  const navigationEventId =
    (location.state as { openEventId?: string } | null)?.openEventId ?? null;

  const hasMore = entries.length < total;

  const handleOpenEventDrawer = (entry: LogEntry) => {
    setSelectedEntry(entry);
    setOpenSideDrawer(true);
  };

  const handleCloseEventDrawer = () => {
    setOpenSideDrawer(false);
    setSelectedEntry(null);
  };

  const handleResetFilters = () => {
    dispatch(resetFilters());
  };

  const handleSort = (sortKey: string) => {
    if (sorting.sortBy === sortKey) {
      dispatch(
        setSorting({
          sortBy: sortKey,
          sortOrder: sorting.sortOrder === 'asc' ? 'desc' : 'asc'
        })
      );
    } else {
      dispatch(setSorting({ sortBy: sortKey, sortOrder: 'asc' }));
    }
  };

  const fetchTrendsData = async () => {
    if (!id) return;

    dispatch(setTrendsLoading(true));
    try {
      const data = await getTrends({ aiSystemId: id, months: 12 });
      dispatch(setTrendsSuccess(data));
    } catch (error) {
      console.error('Failed to fetch trends data:', error);
      dispatch(setTrendsError('Failed to fetch trends data'));
    }
  };

  // `requestIdRef` marks each fetch with an id and discards a response whose
  // id is no longer the latest one in flight when it resolves. Without this,
  // fast typing in the search box (a new request per keystroke once past the
  // 3-char minimum) can have an EARLIER, narrower query's request resolve
  // AFTER a later, more specific one - silently overwriting the correct
  // results with stale ones, which looks like "search sometimes just
  // doesn't work". Same guard as the org-wide Audit Log page (AuditLogPage).
  const requestIdRef = useRef(0);

  const fetchPage = async (offset: number) => {
    if (!id) return;

    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const { userIds, githubUserNames } = splitUserFilterValues(filters.user);

      const response = await getEvents({
        aiSystemId: id,
        limit: EVENTS_PAGE_SIZE,
        offset,
        search: filters.search || undefined,
        types: filters.types.length > 0 ? filters.types : undefined,
        userIds: userIds.length > 0 ? userIds : undefined,
        githubUserNames: githubUserNames.length > 0 ? githubUserNames : undefined,
        startDate: filters.dateRange?.startDate,
        endDate: filters.dateRange?.endDate,
        sortBy: sorting.sortBy,
        sortOrder: sorting.sortOrder
      });

      if (requestIdRef.current !== requestId) return;
      setEntries((prev) => (offset === 0 ? response.items : [...prev, ...response.items]));
      setTotal(response.total);
    } catch (error) {
      console.error('Failed to fetch log events:', error);
      if (requestIdRef.current !== requestId) return;
      setEntries([]);
      setTotal(0);
    } finally {
      if (requestIdRef.current === requestId) setLoading(false);
    }
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    fetchPage(entries.length);
  };

  const handleExport = async (format: 'csv' | 'json' | 'pdf') => {
    if (!id) return;

    try {
      const { userIds, githubUserNames } = splitUserFilterValues(filters.user);
      const exportParams = {
        aiSystemId: id,
        search: filters.search || undefined,
        types: filters.types.length > 0 ? filters.types : undefined,
        userIds: userIds.length > 0 ? userIds : undefined,
        githubUserNames: githubUserNames.length > 0 ? githubUserNames : undefined,
        startDate: filters.dateRange?.startDate,
        endDate: filters.dateRange?.endDate
      };

      let download: { blob: Blob; filename?: string };
      let fileExtension: string;

      switch (format) {
        case 'csv':
          download = await exportEventsCSV(exportParams);
          fileExtension = 'csv';
          break;
        case 'json':
          download = await exportEventsJSON(exportParams);
          fileExtension = 'json';
          break;
        case 'pdf':
          download = await exportEventsPDF(exportParams);
          fileExtension = 'pdf';
          break;
        default:
          return;
      }

      triggerBlobDownload(
        download.blob,
        download.filename ||
          `system-logs-${new Date().toISOString().split('T')[0]}.${fileExtension}`
      );
    } catch (error) {
      console.error(`Failed to download ${format.toUpperCase()}:`, error);
    }
  };

  useEffect(() => {
    fetchTrendsData();

    if (!id) return;
    getAiSystemLogUsers(id)
      .then((users) => setUserOptions(buildUserFilterOptions(users)))
      .catch(() => setUserOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    fetchPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    id,
    filters.search,
    filters.types,
    filters.user,
    filters.dateRange,
    sorting
  ]);

  useEffect(() => {
    hasOpenedEventFromNavigation.current = false;
  }, [id, navigationEventId]);

  useEffect(() => {
    if (!navigationEventId || hasOpenedEventFromNavigation.current) {
      return;
    }

    const matchingEntry = entries.find((entry) => entry.id === navigationEventId);

    if (!matchingEntry) {
      return;
    }

    handleOpenEventDrawer(matchingEntry);
    hasOpenedEventFromNavigation.current = true;
  }, [navigationEventId, entries]);

  return (
    <div className="flex flex-col gap-6 flex-1 max-h-[calc(100vh-300px)] overflow-y-auto p-1">
      <div className="flex items-center justify-between">
        <span className="font-inter font-medium text-base leading-6 text-[#27272A]">
          Logs
        </span>
        <ExportDropdown onExport={handleExport} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="flex flex-col gap-4 lg:col-span-2">
          <EventsCard />
          <OverviewChartCard />
        </div>
        <div className="lg:col-span-3 h-full">
          <TrendsChart aiSystemId={id || ''} />
        </div>
      </div>

      <SearchSection
        searchValue={filters.search}
        onSearchChange={(value) => dispatch(setSearchFilter(value))}
        placeholder="Search by keyword"
      />

      <MonitoringFiltersSection
        selectedTypes={filters.types}
        onTypesChange={(value) => dispatch(setTypesFilter(value))}
        selectedUser={filters.user}
        onUserChange={(value) => dispatch(setUserFilter(value))}
        selectedDateRange={filters.dateRange}
        onDateRangeChange={(value) => dispatch(setDateRangeFilter(value))}
        onResetFilters={handleResetFilters}
        userOptions={userOptions}
      />

      <AuditLogsTable
        entries={entries}
        loading={loading}
        hasMore={hasMore}
        onLoadMore={handleLoadMore}
        onRowClick={handleOpenEventDrawer}
        sortBy={sorting.sortBy || 'date'}
        sortOrder={sorting.sortOrder || 'desc'}
        onSort={handleSort}
      />

      {selectedEntry && (
        <SideDrawer
          isOpen={openSideDrawer}
          onClose={handleCloseEventDrawer}
          title={selectedEntry.action}
          listItems={getAuditLogDrawerContent(selectedEntry)}
        />
      )}
    </div>
  );
}
